// src/services/firebaseDienstplanService.js
// Komplett neu geschriebener Firebase-Dienstplan-Service

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  addDoc,
  query,
  where,
  arrayUnion,
  arrayRemove,
  orderBy
} from "firebase/firestore";
import { db } from "../firebaseConfig";

/**
 * Dienstplan-Einstellungen aus Firestore laden
 */
export const getDienstplanSettings = async (projektId = null) => {
  try {
    let settingsDocId = "settings";
    
    // Wenn projektId angegeben ist, projektspezifische Settings laden
    if (projektId) {
      settingsDocId = `settings-${projektId}`;
    }
    
    const ref = doc(db, "dienstplanSettings", settingsDocId);
    const snap = await getDoc(ref);
    
    if (snap.exists()) {
      return snap.data();
    } else {
      // Fallback: Standard-Settings versuchen
      const standardRef = doc(db, "dienstplanSettings", "settings");
      const standardSnap = await getDoc(standardRef);
      return standardSnap.exists() ? standardSnap.data() : null;
    }
  } catch (error) {
    console.error("Fehler beim Laden der Dienstplan-Settings:", error);
    return null;
  }
};

/**
 * Alle Mitarbeiter für den Dienstplan laden
 */
export const getAllStaffForSchedule = async () => {
  const usersSnap = await getDocs(collection(db, "users"));
  return usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

/**
 * Externen Mitarbeiter zur Datenbank hinzufügen
 */
export const addExternalStaff = async (staff) => {
  try {
    console.log("Füge externen Mitarbeiter hinzu:", staff.name);
    const ref = collection(db, "users");
    
    const staffData = {
      ...staff,
      username: staff.name,
      isExternal: true,
      role: "extern",
      createdAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(ref, staffData);
    console.log("Mitarbeiter hinzugefügt mit ID:", docRef.id);
    return { id: docRef.id, ...staffData };
  } catch (error) {
    console.error("Fehler beim Anlegen des Mitarbeiters:", error);
    throw error;
  }
};

/**
 * Monatliche Schichten laden
 */
export const getMonthlyShifts = async (year, month) => {
  const snapshot = await getDocs(collection(db, "dienstplanung"));
  return snapshot.docs
    .map(doc => doc.data())
    .filter(entry => entry.year === year && entry.month === month);
};

// Hilfsfunktion für Schichtcode
function getShiftCode(pos, schicht) {
  if (pos === "TL" && schicht === "06-18") return "TTF";
  if (pos === "TL" && schicht === "18-06") return "TNF";
  if (pos === "BSH" && schicht === "06-18") return "BTF";
  if (pos === "BSH" && schicht === "18-06") return "BNF";
  if (pos === "ZG" && schicht === "06-18") return "ZTF";
  if (pos === "ZG" && schicht === "18-06") return "ZNF";
  return `${pos}-${schicht}`;
}

/**
 * Monatlichen Dienstplan für ein Projekt laden
 */
export const getDienstplanByMonth = async (year, month, projektId) => {
  try {
    const monthYearDocId = `${year}-${String(month).padStart(2, "0")}`;

    const monthDocRef = doc(db, "dienstplaene", `${monthYearDocId}-${projektId}`);
    const monthSnap = await getDoc(monthDocRef);

    if (!monthSnap.exists()) {
      return { 
        shifts: {},
        projektId: projektId,
        year: year,
        month: month
      };
    }

    return monthSnap.data();
  } catch (error) {
    console.error(`Fehler beim Laden des gesamten Dienstplans für ${year}-${month}:`, error);
    return { shifts: {} };
  }
};

/**
 * Schicht einem Mitarbeiter zuweisen
 */
export const assignShift = async (date, shiftTime, position, userId, projektId, reason = null) => {
  try {
    // Datum parsen und Monat/Jahr extrahieren
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1; // JavaScript Monate sind 0-basiert
    const monthYearDocId = `${year}-${String(month).padStart(2, "0")}`;

    // Referenz zum Monats-Dokument mit Projekt-ID
    const monthDocRef = doc(db, "dienstplaene", `${monthYearDocId}-${projektId}`);

    // Aktuellen Stand des Monats laden
    const monthSnap = await getDoc(monthDocRef);
    const currentData = monthSnap.exists() ? monthSnap.data() : { 
      shifts: {},
      projektId: projektId,
      year: year,
      month: month
    };

    // Sicherstellen, dass die Struktur existiert
    if (!currentData.shifts) currentData.shifts = {};
    if (!currentData.shifts[date]) currentData.shifts[date] = {};
    
    // Für F und U keine shiftTime verwenden (shiftTime kann null sein)
    if (position === "F" || position === "U" || shiftTime === null) {
      if (!currentData.shifts[date][position]) currentData.shifts[date][position] = [];
      
      // Für F den Grund speichern
      if (position === "F" && reason) {
        if (!currentData.shifts[date].reasons) currentData.shifts[date].reasons = {};
        currentData.shifts[date].reasons[userId] = reason;
      }
      
      // Mitarbeiter zur Position hinzufügen (wenn noch nicht vorhanden)
      if (!currentData.shifts[date][position].includes(userId)) {
        currentData.shifts[date][position].push(userId);
      }
    } else {
      // Normale Schichten mit shiftTime
      if (!currentData.shifts[date][shiftTime]) currentData.shifts[date][shiftTime] = {};
      if (!currentData.shifts[date][shiftTime][position]) currentData.shifts[date][shiftTime][position] = [];

      // Mitarbeiter zur Position hinzufügen (wenn noch nicht vorhanden)
      if (!currentData.shifts[date][shiftTime][position].includes(userId)) {
        currentData.shifts[date][shiftTime][position].push(userId);
      }
    }

    // Aktualisiertes Dokument speichern
    await setDoc(monthDocRef, currentData);
    
    console.log(`Schicht erfolgreich zugewiesen für Projekt ${projektId}.`);
  } catch (error) {
    console.error("Fehler beim Zuweisen der Schicht:", error);
    throw error;
  }
};

/**
 * Freiwunsch/Urlaubsantrag speichern
 */
export const saveTimeOffRequest = async (data) => {
  try {
    console.log("Speichere Freiwunsch/Urlaubsantrag:", data);
    
    // Projekt-ID hinzufügen, falls nicht vorhanden
    const requestData = {
      ...data,
      projektId: data.projektId || localStorage.getItem('lastProjektId') || '',
      createdAt: new Date().toISOString()
    };
    
    const ref = doc(collection(db, "timeOffRequests"));
    await setDoc(ref, requestData);
    console.log("Antrag gespeichert mit ID:", ref.id);
    return ref.id;
  } catch (error) {
    console.error("Fehler beim Speichern des Antrags:", error);
    throw error;
  }
};

/**
 * Freiwünsche/Urlaubsanträge für einen Monat und Projekt laden
 */
export const getTimeOffRequestsByMonth = async (year, month, projektId = null) => {
  try {
    // Projekt-ID verwenden oder aus localStorage holen
    const currentProjektId = projektId || localStorage.getItem('lastProjektId') || '';
    
  
    
    const snapshot = await getDocs(collection(db, "timeOffRequests"));
    const requests = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      
      // Prüfe Jahr, Monat, Status und Projekt-ID
      if (data.year === year && 
          data.month === month && 
          (data.status === "approved" || data.status === "pending") &&
          data.projektId === currentProjektId) {

        requests.push({ id: doc.id, ...data });
      }
    });
    

    return requests;
  } catch (error) {
    console.error('Error in getTimeOffRequestsByMonth:', error);
    return [];
  }
};

/**
 * Status eines Urlaubsantrags aktualisieren
 * @param {string} id - Antrags-ID
 * @param {string} status - "approved", "rejected" oder "pending"
 * @param {string} rejectionReason - Grund für Ablehnung (bei status="rejected")
 */
export const updateTimeOffRequestStatus = async (id, status, rejectionReason = null) => {
  const ref = doc(db, "timeOffRequests", id);
  const updateData = { status };
  
  // Wenn es eine Ablehnung ist und ein Ablehnungsgrund angegeben wurde, speichere ihn
  if (status === "rejected" && rejectionReason) {
    updateData.rejectionReason = rejectionReason;
  }
  
  await updateDoc(ref, updateData);
};

/**
 * Eigene Urlaubsanträge eines Benutzers laden
 */
export const getUserTimeOffRequests = async (userId) => {
  try {
    if (!userId) {
      console.log("Keine Benutzer-ID angegeben");
      return [];
    }
    
    console.log(`Lade Anträge für Benutzer ${userId}`);
    
    const q = query(
      collection(db, "timeOffRequests"),
      where("userId", "==", userId)
    );
    
    const snapshot = await getDocs(q);
    console.log(`${snapshot.size} Anträge für Benutzer gefunden`);
    
    const results = snapshot.docs.map((doc) => {
      const data = doc.data();
      // Sicherstellen, dass year und month numerisch sind
      if (typeof data.year === 'string') data.year = parseInt(data.year);
      if (typeof data.month === 'string') data.month = parseInt(data.month);
      
      return {
        id: doc.id,
        ...data
      };
    });
    
    return results;
  } catch (error) {
    console.error(`Fehler beim Laden der Anträge für Benutzer ${userId}:`, error);
    return [];
  }
};

/**
 * Alle offenen (noch nicht genehmigten oder abgelehnten) Urlaubsanträge laden
 */
export const getAllPendingTimeOffRequests = async () => {
  try {
    
    // Hole die aktuelle Projekt-ID aus dem localStorage
    const projektId = localStorage.getItem('lastProjektId') || '';
    let projektName = projektId;
    
    // Hole den Projektnamen aus dem localStorage
    try {
      const projekteRaw = localStorage.getItem('projekte');
      if (projekteRaw) {
        const projekte = JSON.parse(projekteRaw);
        const found = projekte.find(p => p.id === projektId);
        if (found) projektName = found.name;
      }
    } catch (e) { /* ignore */ }
    

    
    // Lade alle pending Anträge und filtere dann nach projektId
    const q = query(
      collection(db, "timeOffRequests"),
      where("status", "==", "pending"),
      orderBy("date", "asc")
    );
    
    const snapshot = await getDocs(q);
    
    const results = snapshot.docs.map((doc) => {
      const data = doc.data();
      // Sicherstellen, dass year und month numerisch sind
      if (typeof data.year === 'string') data.year = parseInt(data.year);
      if (typeof data.month === 'string') data.month = parseInt(data.month);
      
      return {
        id: doc.id,
        ...data
      };
    });
    
    
    // Filtere nach projektId (leere projektId oder korrekte projektId)
    // Berücksichtige sowohl Projektnamen als auch Projekt-IDs
    const filteredResults = results.filter(req => {
      // Wenn kein Projekt zugewiesen ist, zeige alle an
      if (!req.projektId || req.projektId === "") return true;
      
      // Wenn das aktuelle Projekt "fdb13811" oder "FDB 13811" ist, zeige auch Anträge mit der alten Projekt-ID an
      if ((projektName === "fdb13811" || projektName === "FDB 13811") && req.projektId === "PfIuhXZZe0MBxgjyQvcW") return true;
      
      // Zeige Anträge mit dem aktuellen Projektnamen an
      return req.projektId === projektName;
    });
    
    
    return filteredResults;
  } catch (error) {
    console.error("Fehler beim Laden der offenen Anträge:", error);
    return [];
  }
};

// Freiwunsch/Urlaubsantrag löschen
export const deleteTimeOffRequest = async (id) => {
  try {
    await deleteDoc(doc(db, "timeOffRequests", id));
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Antrags:", error);
    throw error;
  }
};

/**
 * Soll-Stärke für Dienstplan in Firestore speichern
 */
export const saveSollstaerkeToFirestore = async (id, data) => {
  try {
    if (!id || !data) throw new Error("ID und Daten sind Pflicht!");
    await setDoc(doc(db, "dienstplanSollstaerke", id), data);
    console.log("Soll-Stärke gespeichert:", data);
    return true;
  } catch (error) {
    console.error("Fehler beim Speichern der Soll-Stärke:", error);
    throw error;
  }
};

/**
 * Alle Soll-Stärken aus Firestore laden
 */
export const getAllSollstaerkenFromFirestore = async () => {
  try {
    const snapshot = await getDocs(collection(db, "dienstplanSollstaerke"));
    const result = [];
    snapshot.forEach(docSnap => {
      result.push({ id: docSnap.id, ...docSnap.data() });
    });
    // Nach Datum und Schicht sortieren (Datum aufsteigend, Tag vor Nacht)
    result.sort((a, b) => {
      if (a.datum === b.datum) {
        return a.schicht.localeCompare(b.schicht);
      }
      return a.datum.localeCompare(b.datum);
    });
    return result;
  } catch (error) {
    console.error("Fehler beim Laden der Soll-Stärken:", error);
    return [];
  }
};

/**
 * Benutzerdefinierte Positionen für die Stärkemeldung aus Firestore laden
 */
export const getCustomStaerkemeldungPositions = async () => {
  try {
    const snapshot = await getDocs(collection(db, "staerkemeldung_custom"));
    const result = [];
    snapshot.forEach(docSnap => {
      result.push({ id: docSnap.id, ...docSnap.data() });
    });
    // Nach Name sortieren
    result.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    return result;
  } catch (error) {
    console.error("Fehler beim Laden der Stärkemeldung-Positionen:", error);
    return [];
  }
};

/**
 * Neue benutzerdefinierte Position für die Stärkemeldung speichern
 */
export const addCustomStaerkemeldungPosition = async (data) => {
  try {
    if (!data || !data.name || !data.name.trim()) throw new Error("Positionsname fehlt");
    const ref = doc(collection(db, "staerkemeldung_custom"));
    await setDoc(ref, data);
    return { id: ref.id, ...data };
  } catch (error) {
    console.error("Fehler beim Hinzufügen der Stärkemeldung-Position:", error);
    throw error;
  }
};

/**
 * Benutzerdefinierte Position für die Stärkemeldung entfernen
 */
export const removeCustomStaerkemeldungPosition = async (id) => {
  try {
    if (!id) throw new Error("Positions-ID fehlt");
    await deleteDoc(doc(db, "staerkemeldung_custom", id));
    return true;
  } catch (error) {
    console.error("Fehler beim Entfernen der Stärkemeldung-Position:", error);
    throw error;
  }
};

/**
 * Alle Projekte laden
 */
export const getAllProjekte = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'projekte'));
    const projekte = [];
    snapshot.forEach(doc => {
      projekte.push({ id: doc.id, ...doc.data() });
    });
    return projekte;
  } catch (error) {
    console.error('Fehler beim Laden der Projekte:', error);
    return [];
  }
};

/**
 * Schichtzuweisung entfernen
 */
export const removeShiftAssignment = async (date, shiftTime, position, userId, projektId) => {
  try {
    // Datum parsen und Monat/Jahr extrahieren
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const monthYearDocId = `${year}-${String(month).padStart(2, "0")}`;

    // Referenz zum Monats-Dokument mit Projekt-ID (alte Struktur für Schichten)
    const monthDocRef = doc(db, "dienstplaene", `${monthYearDocId}-${projektId}`);

    // Aktuellen Stand des Monats laden
    const monthSnap = await getDoc(monthDocRef);
    if (!monthSnap.exists()) {
      return; // Einfach zurückkehren statt Fehler werfen
    }

    const currentData = monthSnap.data();

    // Für F und U: Andere Struktur
    if (position === "F" || position === "U") {
      // Prüfen ob die Schicht existiert
      if (!currentData.shifts?.[date]?.[position]) {
        return;
      }

      // Mitarbeiter aus der Position entfernen
      currentData.shifts[date][position] = 
        currentData.shifts[date][position].filter(id => id !== userId);

      // Wenn keine Mitarbeiter mehr in der Position sind, Position löschen
      if (currentData.shifts[date][position].length === 0) {
        delete currentData.shifts[date][position];
      }

      // Auch den Grund löschen, falls vorhanden
      if (currentData.shifts[date].reasons?.[userId]) {
        delete currentData.shifts[date].reasons[userId];
      }
    } else {
      // Normale Schichten mit shiftTime
      // Prüfen ob die Schicht existiert
      if (!currentData.shifts?.[date]?.[shiftTime]?.[position]) {
        return;
      }

      // Mitarbeiter aus der Position entfernen
      currentData.shifts[date][shiftTime][position] = 
        currentData.shifts[date][shiftTime][position].filter(id => id !== userId);

      // Wenn keine Mitarbeiter mehr in der Position sind, Position löschen
      if (currentData.shifts[date][shiftTime][position].length === 0) {
        delete currentData.shifts[date][shiftTime][position];
      }

      // Wenn keine Positionen mehr in der Schichtzeit sind, Schichtzeit löschen
      if (Object.keys(currentData.shifts[date][shiftTime]).length === 0) {
        delete currentData.shifts[date][shiftTime];
      }
    }

    // Wenn keine Schichtzeiten mehr am Tag sind, Tag löschen
    if (Object.keys(currentData.shifts[date]).length === 0) {
      delete currentData.shifts[date];
    }

    // Aktualisiertes Dokument speichern
    await setDoc(monthDocRef, currentData);
    
  } catch (error) {
    console.error("Fehler beim Entfernen der Schicht:", error);
    throw error;
  }
};

export async function getUsersByProjektId(projektId) {
  try {
    // Erst alle Benutzer laden
    const allUsersSnapshot = await getDocs(collection(db, "users"));
    
    if (allUsersSnapshot.docs.length === 0) {
      return [];
    }
    
    // Nach projektId filtern
    const q = query(collection(db, "users"), where("projektId", "==", projektId));
    const snapshot = await getDocs(q);
    
    const result = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Wenn keine Benutzer gefunden wurden, aber Benutzer mit falscher projektId existieren, diese korrigieren
    if (result.length === 0 && allUsersSnapshot.docs.length > 0) {
      const usersWithWrongProjektId = allUsersSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.projektId === "PfIuhXZZe0MBxgjyQvcW";
      });
      
      if (usersWithWrongProjektId.length > 0) {
        for (const userDoc of usersWithWrongProjektId) {
          try {
            await updateDoc(doc(db, "users", userDoc.id), {
              projektId: projektId
            });
          } catch (error) {
            console.error(`Fehler beim Korrigieren von Benutzer ${userDoc.id}:`, error);
          }
        }
        
        // Nach der Korrektur erneut laden
        const correctedSnapshot = await getDocs(q);
        const correctedResult = correctedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        return correctedResult;
      }
    }
    
    return result;
  } catch (error) {
    console.error('Fehler in getUsersByProjektId:', error);
    return [];
  }
}